import axios, { AxiosRequestConfig } from 'axios';
import { useMemo } from 'react';
import { useAuthContext } from '../../apps/auth/contexts/AuthContext';
import { globalConfig } from '../config/config';
import AppError from '../features/error/services/AppError';

const useAxios = () => {
	const auth = useAuthContext();

	const axiosInstance = useMemo(() => {
		const axiosInstance = axios.create({
			headers: {
				'Content-Type': 'application/json',
			},
		});

		axiosInstance.interceptors.request.use((config) => {
			const token = auth.authAccessToken;
			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
				if (auth.authContext) {
					config.headers['X-Authorization-Context-Type'] = auth.authContext;
				}
				if (auth.currentCompany) {
					config.headers['X-Authorization-Context-CompanyId'] = auth.currentCompany.id.toString();
				}
				if (auth.authOverrideUserId) {
					config.headers['X-Authorization-Context-ImpersonatedUserId'] = auth.authOverrideUserId.toString();
				}
			}

			config.baseURL = globalConfig.get().APP_API_REST_URL;
			config.withCredentials = true;

			return config;
		});
		axiosInstance.interceptors.response.use(
			(response) => {
				return response;
			},
			(error) => {
				return Promise.reject(error);
			},
		);
		return axiosInstance;
	}, [auth]);

	const post = async (url: string, params: unknown = null) => {
		return axiosInstance.post(url, params);
	};

	const remove = async (url: string) => {
		return axiosInstance.delete(url);
	};

	const put = async (url: string, params: unknown) => {
		return axiosInstance.put(url, params);
	};

	const patch = async (url: string, params: unknown) => {
		return axiosInstance.patch(url, params);
	};

	const get = async (url: string, options?: AxiosRequestConfig) => {
		try {
			return axiosInstance.get(url, options);
		} catch (e) {
			throw new AppError(e, 'useAxios[get]');
		}
	};

	const upload = async (url: string, formData: FormData) => {
		return axiosInstance.post(url, formData, {
			headers: { 'content-type': 'multipart/form-data' },
		});
	};

	return { axios: axiosInstance, get, post, put, patch, upload, remove };
};

export default useAxios;
